"use client";

import React, { useState } from "react";
import { Mail, MapPin, Phone, Facebook, Instagram } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useForm } from "react-hook-form";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <NavigationMenu viewport={false} className="items-start justify-start max-w-full">
      <NavigationMenuList className="flex-col items-start gap-3 space-x-0">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="w-full">
            <NavigationMenuLink asChild className="w-full justify-start px-0 py-0 h-auto font-normal text-muted-foreground hover:text-foreground hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-foreground">
              <Link to={item.href || "#"}>{item.label}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function NewsletterForm() {
  const methods = useForm({
    defaultValues: {
      email: ""
    }
  });
  const {
    handleSubmit,
    formState: {
      isSubmitting
    }
  } = methods;
  const [email, setEmail] = useState("");
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit(async () => {
      setEmail("");
    })(e);
  };
  return <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <Input type="email" name="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-Mail-Adresse eingeben" required className="bg-background" aria-label="E-Mail-Adresse für Newsletter" />
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? "Wird abonniert..." : "Abonnieren"}
        </Button>
      </form>
    </FormProvider>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="footer" className="bg-background text-foreground pt-16 pb-8 border-t border-border">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-8 mb-12">
          
          <div className="flex flex-col gap-6">
            <Link to="/" className="inline-block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/image_jxvw8778.png" alt="Cup4us OG Logo" className="h-10 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Mehrwegbecher für nachhaltige Veranstaltungen. Unsere Leistungen umfassen Verleih, Verkauf, Reinigung, Lieferung und Lagerung – alles aus einer Hand in Kärnten, Österreich.
            </p>
            <div className="flex items-center gap-4 mt-2">
              <Link to="https://www.facebook.com/cup4us?locale=de_DE" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Facebook">
                <Facebook className="size-5" />
              </Link>
              <Link to="https://www.instagram.com/cup4us_mehrwegbecher/" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Instagram">
                <Instagram className="size-5" />
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="font-default font-semibold text-lg">Schnellzugriff</h3>
            <MenuProvider menu_id="26">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="font-default font-semibold text-lg">Kontakt</h3>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3 text-muted-foreground text-sm">
                <MapPin className="size-5 shrink-0 text-primary" />
                <span>Klagenfurter Straße 67, 9300 St. Veit/Glan</span>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground text-sm">
                <Phone className="size-5 shrink-0 text-primary" />
                <Link to="tel:+431234567890" className="hover:text-foreground transition-colors">+43 660 933 76 67</Link>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground text-sm">
                <Mail className="size-5 shrink-0 text-primary" />
                <Link to="mailto:info@cup4us.at" className="hover:text-foreground transition-colors">office@cup4us.at</Link>
              </li>
            </ul>
          </div>

        </div>

        <Separator className="mb-8" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {currentYear} Cup4us OG. Alle Rechte vorbehalten.</p>
          <div className="flex items-center gap-6">
            <Link to="#" className="hover:text-foreground transition-colors">
              Datenschutzerklärung
            </Link>
            <Link to="#" className="hover:text-foreground transition-colors">
              Impressum
            </Link>
          </div>
        </div>
      </div>
    </section>;
}